import React, { useState, useEffect } from 'react';
import { Package, Plus, Edit2, Trash2, Save, X, AlertTriangle, CheckCircle } from 'lucide-react';

export default function ProductKnowledgeSection({ products, onChange }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '',
    category: 'general',
    accepted: true,
    shippingMethod: 'groupage',
    restrictions: '',
    requiredDocuments: '',
    specialInstructions: '',
    examples: ''
  });
  const [isAdding, setIsAdding] = useState(false);

  const productCategories = [
    { value: 'general', label: 'Général' },
    { value: 'electronics', label: 'Électronique & Électroménager' },
    { value: 'vehicles', label: 'Véhicules' },
    { value: 'furniture', label: 'Meubles & Décoration' },
    { value: 'food', label: 'Denrées Alimentaires' },
    { value: 'clothing', label: 'Vêtements & Textiles' },
    { value: 'documents', label: 'Documents & Papiers' },
    { value: 'prohibited', label: 'Produits Interdits' }
  ];

  const shippingMethods = [
    { value: 'groupage', label: 'Groupage (LCL)' },
    { value: 'container', label: 'Conteneur Complet (FCL)' },
    { value: 'both', label: 'Groupage ou Conteneur' },
    { value: 'special', label: 'Procédure Spéciale' }
  ];

  const defaultProducts = [
    {
      id: 1,
      name: 'Cartons (Plusieurs Tailles Disponibles)',
      category: 'general',
      accepted: true,
      shippingMethod: 'groupage',
      restrictions: 'Nous proposons 3 tailles de cartons avec des tarifs différents. CRITIQUE: Quand un client demande "combien pour un carton?" sans préciser la taille, Fatou DOIT OBLIGATOIREMENT demander quelle taille avant de donner le prix. Ne JAMAIS donner un prix vague ou approximatif.',
      requiredDocuments: 'Liste de colisage',
      specialInstructions: 'TAILLES ET TARIFS (À COMMUNIQUER DIRECTEMENT):\n\n- Petit carton (60x40x40 cm, jusqu\'\u00e0 15kg): 90€ NET\n- Carton moyen (80x60x60 cm, jusqu\'\u00e0 25kg): 120€ NET\n- Grand carton (100x80x80 cm, jusqu\'\u00e0 35kg): 180€ NET\n\n⚠️ QUESTION DE CLARIFICATION OBLIGATOIRE:\nSi le client demande "combien pour un carton?" sans préciser la taille, Fatou doit répondre:\n"Chef, on a trois tailles de cartons avec des prix différents! 📦\n- Petit carton (60x40cm): 90€\n- Moyen carton (80x60cm): 120€\n- Grand carton (100x80cm): 180€\nTu veux envoyer quelle taille? Quel poids approximativement?"\n\n✅ APRÈS LA CLARIFICATION: Donne le prix exact de la taille choisie + mentionne les promotions actives',
      examples: 'Petit carton pour vêtements/chaussures, carton moyen pour électronique, grand carton pour objets volumineux'
    },
    {
      id: 8,
      name: 'Barriques — Offre Été 2026 (PRIORITAIRE)',
      category: 'general',
      accepted: true,
      shippingMethod: 'groupage',
      restrictions: '⚠️ RÈGLE ABSOLUE : Toujours proposer l\'Offre Été 2026 en PREMIER pour la barrique noire 270L. Ne jamais répondre avec les anciens tarifs (25€/30€/140€) comme réponse principale. Mots-clés déclencheurs : barrique, barique, barigo, fût, fut, tonneau, bidon, barrique noire, barrique 270L.',
      requiredDocuments: 'Aucun document requis pour l\'achat de barrique vide',
      specialInstructions: '🎁 OFFRE ÉTÉ 2026 — RÉPONSE PRIORITAIRE :\n\nQuand un client parle de barrique, répondre EN PRIORITÉ :\n"Bonne nouvelle 🎁 En ce moment avec l\'Offre Été 2026, la barrique noire 270L est OFFERTE !\nTu paies seulement 160€ TTC tout compris : transport France vers Abidjan, dédouanement et livraison à Abidjan inclus.\n\nOn te livre la barrique vide chez toi, tu paies 50€ à la livraison, tu la remplis, puis on vient la collecter et tu règles les 110€ restants.\n\nTu veux en profiter ? 😊"\n\n📦 PRISE DE COMMANDE (ordre strict) :\n1. Nom du client\n2. Téléphone WhatsApp\n3. Adresse complète (numéro + rue + code postal + ville)\n4. Date souhaitée\n5. Créneau (matin 9h-13h / après-midi 14h-18h / soirée après le travail)\n\n🗺️ HORS ÎLE-DE-FRANCE :\nSi adresse hors IDF → ne pas confirmer automatiquement → transmettre à l\'équipe.\n\n🌍 HORS CÔTE D\'IVOIRE :\nSi destination hors CI → ADT Express ne l\'assure pas directement → orienter vers partenaires.\n\n⚠️ ANCIENS TARIFS (ne pas mettre en avant) :\n- Barrique noire vide Paris : 25€/30€ (ancien)\n- Barrique noire vide Banlieue : 30€/35€ (ancien)\n- Transport barrique noire : 140€/150€ (ancien)\nCes tarifs ne doivent PAS être la réponse principale.',
      examples: 'Barrique noire 270L pour gros volumes (vêtements, chaussures, denrées alimentaires, petits objets). Synonymes : barique, barigo, fût, fut, tonneau, bidon.'
    },
    {
      id: 2,
      name: 'Véhicules (Voitures, Motos)',
      category: 'vehicles',
      accepted: true,
      shippingMethod: 'container',
      restrictions: 'Conteneur complet obligatoire (20 ou 40 pieds selon taille). Véhicule en bon état de marche. Vidange et nettoyage complet requis.',
      requiredDocuments: 'Carte grise, certificat de non-gage, facture d\'achat',
      specialInstructions: 'Taxes douanières spécifiques en Côte d\'Ivoire. Dédouanement véhicule avec procédure particulière. Délai: 4-6 semaines.',
      examples: 'Voitures de tourisme, SUV, motos, scooters'
    },
    {
      id: 3,
      name: 'Réfrigérateurs & Gros Électroménager',
      category: 'electronics',
      accepted: true,
      shippingMethod: 'both',
      restrictions: 'Modèles standards (<2m): groupage possible. Très gros modèles (réfrigérateur américain, congélateur industriel): conteneur complet. Emballage renforcé obligatoire.',
      requiredDocuments: 'Facture d\'achat, certificat de conformité (si neuf)',
      specialInstructions: 'Vidange et nettoyage complet avant expédition. Dégivrage 24h avant. Fixation sécurisée pour transport maritime.',
      examples: 'Réfrigérateurs, congélateurs, machines à laver, lave-vaisselle, cuisinières'
    },
    {
      id: 4,
      name: 'Meubles & Décoration',
      category: 'furniture',
      accepted: true,
      shippingMethod: 'both',
      restrictions: 'Démontage recommandé pour économiser l\'espace. Emballage soigné avec protection anti-chocs.',
      requiredDocuments: 'Facture ou liste de colisage',
      specialInstructions: 'Meubles volumineux: possibilité de groupage ou conteneur selon volume total. Protection des surfaces fragiles.',
      examples: 'Canapés, tables, chaises, armoires, lits, tableaux, miroirs'
    },
    {
      id: 5,
      name: 'Denrées Alimentaires Non Périssables',
      category: 'food',
      accepted: true,
      shippingMethod: 'groupage',
      restrictions: 'Uniquement produits non périssables. Emballage hermétique obligatoire. Vérifier réglementation douanière ivoirienne.',
      requiredDocuments: 'Liste détaillée des produits, certificat sanitaire (si requis)',
      specialInstructions: 'Produits frais/congelés interdits. Respecter dates de péremption longues (>6 mois recommandé).',
      examples: 'Conserves, pâtes, riz, huiles, épices, café, thé, chocolat'
    },
    {
      id: 6,
      name: 'Vêtements & Textiles',
      category: 'clothing',
      accepted: true,
      shippingMethod: 'groupage',
      restrictions: 'Emballage dans cartons ou barriques. Protection contre l\'humidité recommandée.',
      requiredDocuments: 'Liste de colisage, factures (si neufs)',
      specialInstructions: 'Vêtements d\'occasion acceptés. Nettoyage préalable recommandé. Pas de limite de quantité pour usage personnel.',
      examples: 'Vêtements, chaussures, sacs, linge de maison, tissus'
    },
    {
      id: 7,
      name: 'Produits Interdits',
      category: 'prohibited',
      accepted: false,
      shippingMethod: 'special',
      restrictions: 'STRICTEMENT INTERDITS - Refus d\'expédition',
      requiredDocuments: 'N/A',
      specialInstructions: 'Ces produits ne peuvent pas être transportés sous aucune condition. Sanctions légales en cas de tentative.',
      examples: 'Armes et munitions, drogues et substances illicites, produits explosifs/inflammables, produits chimiques dangereux, contrefaçons, animaux vivants'
    }
  ];

  // Initialize with default products if empty
  React.useEffect(() => {
    if (!products || products?.length === 0) {
      onChange(defaultProducts);
    }
  }, []);

  const handleAdd = () => {
    if (newProduct?.name?.trim()) {
      onChange([...(products || []), { ...newProduct, id: Date.now() }]);
      setNewProduct({
        name: '',
        category: 'general',
        accepted: true,
        shippingMethod: 'groupage',
        restrictions: '',
        requiredDocuments: '',
        specialInstructions: '',
        examples: ''
      });
      setIsAdding(false);
    }
  };

  const handleEdit = (index, field, value) => {
    const updated = [...(products || [])];
    updated[index] = { ...updated?.[index], [field]: value };
    onChange(updated);
  };

  const handleDelete = (index) => {
    const updated = (products || [])?.filter((_, i) => i !== index);
    onChange(updated);
  };

  const currentProducts = products || defaultProducts;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Package className="text-green-600" size={24} />
          <h2 className="text-xl font-semibold text-gray-900">Base de Connaissances Produits</h2>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus size={18} />
          <span>Ajouter Produit</span>
        </button>
      </div>
      <p className="text-gray-600 text-sm mb-6">
        Configurez les règles d'expédition, restrictions et documents requis pour chaque type de produit. Fatou utilisera ces informations pour répondre précisément aux questions sur les produits acceptés.
      </p>
      {/* Add New Product Form */}
      {isAdding && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <h3 className="font-medium text-gray-900 mb-3">Nouveau Produit</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du Produit/Catégorie
                </label>
                <input
                  type="text"
                  value={newProduct?.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e?.target?.value })}
                  placeholder="Ex: Ordinateurs portables"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  value={newProduct?.category}
                  onChange={(e) => setNewProduct({ ...newProduct, category: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {productCategories?.map(cat => (
                    <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Accepté?
                </label>
                <select
                  value={newProduct?.accepted ? 'true' : 'false'}
                  onChange={(e) => setNewProduct({ ...newProduct, accepted: e?.target?.value === 'true' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  <option value="true">✅ Oui - Accepté</option>
                  <option value="false">❌ Non - Interdit</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Méthode d'Expédition
                </label>
                <select
                  value={newProduct?.shippingMethod}
                  onChange={(e) => setNewProduct({ ...newProduct, shippingMethod: e?.target?.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {shippingMethods?.map(method => (
                    <option key={method?.value} value={method?.value}>{method?.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Restrictions & Conditions
              </label>
              <textarea
                value={newProduct?.restrictions}
                onChange={(e) => setNewProduct({ ...newProduct, restrictions: e?.target?.value })}
                placeholder="Ex: Emballage renforcé obligatoire, dimensions maximales..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Documents Requis
              </label>
              <input
                type="text"
                value={newProduct?.requiredDocuments}
                onChange={(e) => setNewProduct({ ...newProduct, requiredDocuments: e?.target?.value })}
                placeholder="Ex: Facture d'achat, certificat de conformité"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Instructions Spéciales
              </label>
              <textarea
                value={newProduct?.specialInstructions}
                onChange={(e) => setNewProduct({ ...newProduct, specialInstructions: e?.target?.value })}
                placeholder="Ex: Procédure douanière spécifique, délais particuliers..."
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exemples
              </label>
              <input
                type="text"
                value={newProduct?.examples}
                onChange={(e) => setNewProduct({ ...newProduct, examples: e?.target?.value })}
                placeholder="Ex: Téléviseurs, ordinateurs, tablettes"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAdd}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Save size={16} />
                <span>Enregistrer</span>
              </button>
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewProduct({
                    name: '',
                    category: 'general',
                    accepted: true,
                    shippingMethod: 'groupage',
                    restrictions: '',
                    requiredDocuments: '',
                    specialInstructions: '',
                    examples: ''
                  });
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                <X size={16} />
                <span>Annuler</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Products List */}
      <div className="space-y-4">
        {currentProducts?.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
            <Package size={48} className="mx-auto text-gray-400 mb-3" />
            <p className="text-gray-600">Aucun produit configuré</p>
            <p className="text-sm text-gray-500 mt-1">Cliquez sur "Ajouter Produit" pour commencer</p>
          </div>
        ) : (
          currentProducts?.map((product, index) => (
            <div 
              key={product?.id || index} 
              className={`border rounded-lg p-4 ${
                product?.accepted 
                  ? 'bg-white border-gray-200' :'bg-red-50 border-red-200'
              }`}
            >
              {editingIndex === index ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom du Produit/Catégorie
                      </label>
                      <input
                        type="text"
                        value={product?.name}
                        onChange={(e) => handleEdit(index, 'name', e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Catégorie
                      </label>
                      <select
                        value={product?.category}
                        onChange={(e) => handleEdit(index, 'category', e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        {productCategories?.map(cat => (
                          <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Accepté?
                      </label>
                      <select
                        value={product?.accepted ? 'true' : 'false'}
                        onChange={(e) => handleEdit(index, 'accepted', e?.target?.value === 'true')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value="true">✅ Oui - Accepté</option>
                        <option value="false">❌ Non - Interdit</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Méthode d'Expédition
                      </label>
                      <select
                        value={product?.shippingMethod}
                        onChange={(e) => handleEdit(index, 'shippingMethod', e?.target?.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        {shippingMethods?.map(method => (
                          <option key={method?.value} value={method?.value}>{method?.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Restrictions & Conditions
                    </label>
                    <textarea
                      value={product?.restrictions}
                      onChange={(e) => handleEdit(index, 'restrictions', e?.target?.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Documents Requis
                    </label>
                    <input
                      type="text"
                      value={product?.requiredDocuments}
                      onChange={(e) => handleEdit(index, 'requiredDocuments', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Instructions Spéciales
                    </label>
                    <textarea
                      value={product?.specialInstructions}
                      onChange={(e) => handleEdit(index, 'specialInstructions', e?.target?.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Exemples
                    </label>
                    <input
                      type="text"
                      value={product?.examples}
                      onChange={(e) => handleEdit(index, 'examples', e?.target?.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingIndex(null)}
                      className="flex items-center space-x-2 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                    >
                      <Save size={14} />
                      <span>Enregistrer</span>
                    </button>
                    <button
                      onClick={() => setEditingIndex(null)}
                      className="flex items-center space-x-2 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
                    >
                      <X size={14} />
                      <span>Annuler</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {product?.accepted ? (
                          <CheckCircle className="text-green-600" size={20} />
                        ) : (
                          <AlertTriangle className="text-red-600" size={20} />
                        )}
                        <h3 className="font-semibold text-gray-900">{product?.name}</h3>
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          {productCategories?.find(c => c?.value === product?.category)?.label}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Méthode: </span>
                          <span className="text-gray-600">
                            {shippingMethods?.find(m => m?.value === product?.shippingMethod)?.label}
                          </span>
                        </div>
                        {product?.restrictions && (
                          <div>
                            <span className="font-medium text-gray-700">Restrictions: </span>
                            <span className="text-gray-600">{product?.restrictions}</span>
                          </div>
                        )}
                        {product?.requiredDocuments && (
                          <div>
                            <span className="font-medium text-gray-700">Documents: </span>
                            <span className="text-gray-600">{product?.requiredDocuments}</span>
                          </div>
                        )}
                        {product?.specialInstructions && (
                          <div>
                            <span className="font-medium text-gray-700">Instructions: </span>
                            <span className="text-gray-600">{product?.specialInstructions}</span>
                          </div>
                        )}
                        {product?.examples && (
                          <div>
                            <span className="font-medium text-gray-700">Exemples: </span>
                            <span className="text-gray-600 italic">{product?.examples}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <button
                        onClick={() => setEditingIndex(index)}
                        className="text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(index)}
                        className="text-red-600 hover:text-red-700 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-6">
        <p className="text-sm text-purple-800">
          <strong>💡 Importance:</strong> Cette base de connaissances permet à Fatou de donner des réponses précises et cohérentes sur les produits acceptés, leurs conditions d'expédition et les documents requis. Mettez à jour régulièrement pour refléter vos politiques actuelles.
        </p>
      </div>
    </div>
  );
}